import type { YAXisComponentOption } from 'echarts';

import type { TimeRangeLane } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';
import { getTimeRangeLaneColor } from '@/shared/charts/time-series-chart/theme/getTimeRangeLaneColor';
import type { TimeSeriesChartTheme } from '@/shared/charts/time-series-chart/theme/TimeSeriesChartTheme.types';

import { chartLayout } from '../config/chartLayout';
import { trackLabelVisuals } from '../config/chartVisuals';
import { toTranslucentColor } from '../utils/toTranslucentColor';

type CategoryYAxisOption = Extract<YAXisComponentOption, { type?: 'category' }>;
type TrackLabelOption = NonNullable<CategoryYAxisOption['axisLabel']>;

type BuildTrackLabelOptionParams = {
  lanes: TimeRangeLane[];
  highlightedLanes: TimeRangeLane[];
  theme: TimeSeriesChartTheme;
};

function getMarkerStyleName(laneIndex: number) {
  return `marker${laneIndex}`;
}

/** ECharts rich text: one named style per lane marker, plus the gap between marker and label. */
function buildRichStyles(lanes: TimeRangeLane[], theme: TimeSeriesChartTheme) {
  const rich: Record<string, Record<string, unknown>> = {
    gap: {
      width: trackLabelVisuals.markerGap,
    },
  };

  lanes.forEach((lane, laneIndex) => {
    rich[getMarkerStyleName(laneIndex)] = {
      width: trackLabelVisuals.markerSize,
      height: trackLabelVisuals.markerSize,
      borderRadius: theme.trackLabel.markerBorderRadius,
      backgroundColor: getTimeRangeLaneColor(lane, theme),
    };
  });

  return rich;
}

/**
 * Where the label box starts, measured from the track's axis at the grid's left edge; negative when
 * the marker sits inside the grid's side inset. The box begins with its backdrop padding, so it is
 * pulled back by that much: the marker itself then lands on trackLabelMarkerLeft.
 */
const TRACK_LABEL_MARGIN =
  chartLayout.trackLabelMarkerLeft - chartLayout.gridSideInset - trackLabelVisuals.backdropPadding[1];

/**
 * Lane labels with a colour square, drawn inside the track so the time axis can start at the grid's
 * edge. Highlighted lanes take their own colour.
 */
export function buildTrackLabelOption({
  lanes,
  highlightedLanes,
  theme,
}: BuildTrackLabelOptionParams): TrackLabelOption {
  const highlightedLaneIds = new Set(highlightedLanes.map((lane) => lane.id));

  return {
    inside: true,
    align: 'left',
    margin: TRACK_LABEL_MARGIN,
    backgroundColor: toTranslucentColor(theme.trackLabel.backdropColor, trackLabelVisuals.backdropOpacity),
    padding: [...trackLabelVisuals.backdropPadding],
    borderRadius: trackLabelVisuals.backdropBorderRadius,
    formatter: (label: string | number, laneIndex = 0) => `{${getMarkerStyleName(laneIndex)}|}{gap|}${label}`,
    color: (_label?: string | number, laneIndex = 0) => {
      const lane = lanes[laneIndex];

      return lane && highlightedLaneIds.has(lane.id) ? (lane.color ?? theme.trackLabel.color) : theme.trackLabel.color;
    },
    rich: buildRichStyles(lanes, theme),
  };
}
